/**
 * ================================================================
 * API SERVICE: IPOService
 * ================================================================
 *
 * @service          ipoApi
 * @description      API service for IPO management (details, bids, physical applications, documents)
 * @baseUrl          IPO_URL environment variable
 *
 * @endpoints
 * - POST   /ipo/v2/get-ipo-details - Get IPO details (current/upcoming)
 * - POST   /ipo/v2/ipo/details/symbol - Get IPO details by symbol
 * - POST   /ipo/v1/bid/bidSubmit - Submit IPO bid
 * - GET    /ipo/v1/get-client-wise-bid-report - Get client-wise bid report
 * - PUT    /ipo/v1/update-ipo-mandate-status - Update bid status
 * - GET    /ipo/v1/fetch-client-details - Get client details
 * - GET    /apb/v1/user/client_list - Get client list for autocomplete
 * - GET    /ipo/v1/sample-file-physical-csv - Download sample CSV
 * - POST   /ipo/v2/get-ipo-physical-pdf - Get physical IPO print
 * - POST   /ipo/v1/bid/bulk-applications - Bulk bid physical applications
 *
 * @authentication  AuthToken header required for all endpoints
 * @rateLimit        Standard API rate limiting applies
 *
 * @caching
 * - Strategy: RTK Query automatic caching with feature-based tags
 * - Tags: ['IPO', 'BidReport', 'ClientData', 'IPODetails']
 * - TTL: Variable based on data type
 *
 * @errorHandling
 * - Global handler: transformApiError for consistent error management
 * - Auth errors: Automatic session clearing and login redirect
 * - Network errors: User-friendly error messages with retry options
 *
 * @types
 * - All request/response types imported from @/types/ipo
 * - Centralized type management for better maintainability
 *
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { commonParams } from '@/lib/api/baseConfig';
import { createApiErrorTransformer, createBaseQuery, handleApiSuccess } from '@/lib/api/baseQuery';
import type {
  APIResponse,
  BidReportParams,
  BidReportResponse,
  BidSubmission,
  ClientDetailsResponse,
  ClientListParams,
  ClientListResponse,
  IPODetails,
  IPODetailsResponse,
  IPOResponse,
} from '@/types/ipo';
import type { BulkPhysicalSubmitResponse } from '@/types/ipo';
import type { IPOAnalyticsApiResponse } from '@/types/ipo/performance';
import type { IPODashboardCountResponse } from '@/types/ipo/dashboard';
import type {
  IpoFinancialYearParams,
  IpoSummaryDetailsParams,
  IpoSummaryDetailsResponse,
} from '@/types/ipo/summaryDetails';

const IPO_URL = import.meta.env.VITE_APP_IPO_URL;

// ================================================================
// UTILITY FUNCTIONS
// ================================================================
//
// Purpose: Transform API responses from snake_case to camelCase
// Maintains coding consistency across the application
// ================================================================

/**
 * Convert snake_case keys to camelCase recursively
 *
 * @param obj - Object to transform
 * @returns Object with camelCase keys
 */
function toCamelCase(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  }

  const camelCaseObj: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Handle both underscore conversion and manual field mappings
      let camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

      // Manual field mappings for specific API fields that don't follow snake_case
      const fieldMappings: { [key: string]: string } = {
        lotsize: 'lotSize',
        minprice: 'minPrice',
        cutoffprice: 'cutoffPrice',
        lotsizequantity: 'lotSizeQuantity',
        biddingstartdate: 'biddingStartDate',
        biddingenddate: 'biddingEndDate',
      };

      // Use manual mapping if available, otherwise use regex conversion
      if (fieldMappings[key]) {
        camelKey = fieldMappings[key];
      }

      camelCaseObj[camelKey] = toCamelCase(obj[key]);
    }
  }
  return camelCaseObj;
}

/**
 * Convert a Base64 string into a Blob
 * @param base64 Base64-encoded string (no data: prefix required)
 * @param contentType MIME type of the resulting blob
 */
function base64ToBlob(base64: string, contentType = 'application/zip'): Blob {
  // Remove any whitespace/newlines
  const cleaned = base64.replace(/\s/g, '');
  const byteCharacters = atob(cleaned);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
}

export const ipoApi = createApi({
  reducerPath: 'ipoApi',
  baseQuery: createBaseQuery(IPO_URL),
  tagTypes: ['IPO', 'BidReport', 'ClientData', 'IPODetails'],
  endpoints: (builder) => ({
    // Get IPO dashboard count summary
    // Endpoint: GET /ipo/v2/ipo/dashboard-count
    // Returns: IPODashboardCountResponse
    getIpoDashboardCount: builder.query<IPODashboardCountResponse, IpoFinancialYearParams>({
      query: ({ financeYear, source = 'ap' }) => ({
        url: `/ipo/v3/ipo/dashboard-count`,
        method: 'GET',
        params: { financialYear: financeYear, source },
      }),
      providesTags: ['IPO'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('IpoDashboardCount', true),
    }),

    // Get IPO performance analytics summary
    getIpoPerformanceAnalytics: builder.query<IPOAnalyticsApiResponse, IpoFinancialYearParams>({
      query: ({ financeYear, source = 'ap' }) => ({
        url: `/ipo/v3/get-ipo-summary`,
        method: 'GET',
        params: { financialYear: financeYear, source },
      }),
      providesTags: ['IPO'],
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('IpoPerformanceAnalytics', true),
    }),

    // Get IPO summary details for a specific metric (used by ConnectDataTable in dialogs)
    // Endpoint: GET /ipo/v2/get-ipo-summary-details
    // Accepts query params: financial_year, source, metric, page_size, page_number, search_term
    getIpoSummaryDetails: builder.query<IpoSummaryDetailsResponse, IpoSummaryDetailsParams>({
      query: ({
        financialYear,
        source = 'ap',
        metric,
        pageSize = 10,
        pageNumber = 1,
        searchTerm = '',
        filterColumn,
      }) => ({
        url: `/ipo/v3/get-ipo-summary-details`,
        method: 'GET',
        params: {
          financialYear: financialYear,
          source,
          FilterValue: metric,
          pageSize: pageSize,
          pageNumber: pageNumber,
          SearchValue: searchTerm,
          FilterColumn: filterColumn,
        },
      }),
      providesTags: ['IPODetails'],
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('IpoSummaryDetails', true),
    }),

    // Get IPO details (current/upcoming) with camelCase transformation
    getIpoDetails: builder.mutation<IPODetailsResponse, IPODetails>({
      query: (body) => ({
        url: `/ipo/v3/get-ipo-details`,
        method: 'POST',
        params: commonParams,
        body,
      }),
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('IpoDetails', false),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {}
      },
    }),

    // Get specific IPO details by symbol
    getIPODetailsBySymbol: builder.mutation<APIResponse<IPOResponse>, { symbol: string }>({
      query: (body) => ({
        url: `/ipo/v3/ipo/details/symbol`,
        method: 'POST',
        params: commonParams,
        body,
      }),
      invalidatesTags: ['IPO', 'IPODetails'],
      transformErrorResponse: createApiErrorTransformer('IpoDetailsBySymbol', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoDetailsBySymbol', 'IPO symbol details loaded.', false);
        } catch {}
      },
    }),

    // Submit IPO bid
    // Note: Parses response deterministically based on submitted amount
    // - Retail (amount <= 500000): JSON
    // - HNI (amount > 500000): PDF Blob (for download)
    submitBid: builder.mutation<any, { params: { client_code: string }; body: BidSubmission }>({
      query: ({ params, body }) => ({
        url: '/ipo/v1/bid/bidSubmit',
        method: 'POST',
        body,
        params: { ...params, source: 'ap' },
        responseHandler: async (response) => {
          if (body.amount > 500000) {
            // HNI path: return Blob for consumer to download
            return await response.blob();
          }
          // Retail path: parse as JSON
          return await response.json();
        },
      }),
      invalidatesTags: ['BidReport'],
      transformErrorResponse: createApiErrorTransformer('IpoBidSubmit', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoBidSubmit', 'IPO Bid submitted successfully.', true);
        } catch {}
      },
    }),

    // Get client-wise bid report
    getBidReport: builder.query<BidReportResponse, BidReportParams>({
      query: (params) => ({
        url: '/ipo/v1/get-client-wise-bid-report',
        params: { ...params, source: 'ap' },
      }),
      providesTags: ['BidReport'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('IpoBidReport', false),
    }),

    // Update bid status
    updateBidStatus: builder.mutation<any, any>({
      query: () => ({
        url: '/ipo/v1/update-ipo-mandate-status',
        method: 'PUT',
        params: { source: 'ap', exchange: '' },
      }),
      invalidatesTags: ['BidReport'],
      transformErrorResponse: createApiErrorTransformer('IpoBidStatusUpdate', false),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoBidStatusUpdate', 'Bid status updated.', false);
        } catch {}
      },
    }),

    // Get client details
    getClientDetails: builder.query<ClientDetailsResponse, { client_code: string }>({
      query: (params) => ({
        url: '/ipo/v1/fetch-client-details',
        params: { ...params, source: 'ap' },
      }),
      providesTags: ['ClientData'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('IpoClientDetails', true),
    }),

    // Get client list for autocomplete (using different base URL)
    getClientList: builder.query<ClientListResponse, ClientListParams>({
      query: (params) => ({
        url: `${import.meta.env.VITE_API_BASE_URL}/kyc/clients/search`,
        params: { searchQuery: params.code_part },
      }),
      providesTags: ['ClientData'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('IpoClientList', false),
    }),

    // Download sample CSV
    downloadSampleCsv: builder.mutation<Blob, any>({
      query: (params = {}) => ({
        url: '/ipo/v1/sample-file-physical-csv',
        params: { ...params, source: 'ap' },
        responseHandler: (response) => response.blob(),
      }),
      transformErrorResponse: createApiErrorTransformer('IpoSampleCsv', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoSampleCsv', 'Sample CSV downloaded.', true);
        } catch {}
      },
    }),

    // Get physical IPO print (single) - handle JSON base64 or direct blob
    getPhysicalPrint: builder.mutation<Blob, { body: FormData; params?: any }>({
      query: ({ body, params = {} }) => ({
        url: `${IPO_URL}/ipo/v2/get-ipo-physical-pdf`,
        method: 'POST',
        body,
        params: { ...params, source: 'ap' },
        responseHandler: async (response) => {
          // Try to detect content type first
          const contentType = response.headers.get('content-type')?.toLowerCase() || '';

          // If server returned JSON with base64 content
          if (contentType.includes('application/json')) {
            const json = await response.json().catch(() => null);
            if (json && (json.data || json.base64)) {
              const b64 = (json.data || json.base64) as string;
              // Infer mime from filename if available
              const filename: string | undefined = json.filename || json.fileName;
              const ext = filename?.toLowerCase().endsWith('.pdf')
                ? 'application/pdf'
                : 'application/zip';
              return base64ToBlob(b64, ext);
            }
            // If JSON but not base64 payload, fallback to blob of the JSON (unlikely desired)
            return new Blob([JSON.stringify(json ?? {})], { type: 'application/json' });
          }

          // If server streams binary directly
          const blob = await response.blob();
          return blob;
        },
      }),
      transformErrorResponse: createApiErrorTransformer('IpoPhysicalPrint', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoPhysicalPrint', 'Physical print generated.', true);
        } catch {}
      },
    }),

    // Bulk Bid physical IPO applications
    submitBulkOnline: builder.mutation<BulkPhysicalSubmitResponse, FormData>({
      query: (body) => ({
        url: '/ipo/v1/bid/bulk-applications',
        method: 'POST',
        body,
        params: { source: 'ap' },
      }),
      transformErrorResponse: createApiErrorTransformer('IpoBulkSubmit', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoBulkSubmit', 'Bulk physical bids submitted.', true);
        } catch {}
      },
    }),
  }),
});

export const {
  useGetIpoDetailsMutation,
  useGetIPODetailsBySymbolMutation,
  useSubmitBidMutation,
  useGetBidReportQuery,
  useUpdateBidStatusMutation,
  useGetClientDetailsQuery,
  useGetClientListQuery,
  useDownloadSampleCsvMutation,
  useGetPhysicalPrintMutation,
  useSubmitBulkOnlineMutation,
  useGetIpoPerformanceAnalyticsQuery,
  useGetIpoDashboardCountQuery,
  useGetIpoSummaryDetailsQuery,
} = ipoApi;
